import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Bell, Settings, LifeBuoy, LogOut, Sun, Moon, UserCircle2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { BrandLogo } from "@/components/BrandLogo";
import { UserAvatar, resolveAvatarUrl } from "@/components/UserAvatar";

export function MobileTopHeader({ onOpenNotifications, unreadCount = 0 }) {
  const { me, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 4);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!profileOpen) return;
    function onDown(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [profileOpen]);

  return (
    <div
      data-testid="mobile-top-header"
      className="sticky top-0 left-0 z-50 w-full"
    >
      <div
        data-testid="mobile-top-header-card"
        className={[
          "flex h-[56px] items-center justify-between gap-2 px-4 transition-all duration-300",
          "border-b backdrop-blur-2xl",
          scrolled
            ? "border-zinc-200/40 bg-white/70 shadow-[0_8px_24px_-10px_rgba(15,23,42,0.18)] dark:border-white/[0.05] dark:bg-[#0a0a0a]/70 dark:shadow-[0_8px_24px_-8px_rgba(0,0,0,0.5)]"
            : "border-zinc-200/60 bg-white/85 dark:border-white/[0.05] dark:bg-[#0a0a0a]/85",
        ].join(" ")}
      >
        {/* Left: Logo */}
        <NavLink to="/" data-testid="mobile-header-logo-link" className="flex shrink-0 items-center">
          <BrandLogo
            plain
            iconSize={36}
            testId="mobile-header-logo"
            textClassName="text-[14px] font-extrabold tracking-tight text-zinc-900 dark:text-white"
          />
        </NavLink>

        {/* Right: Actions */}
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            data-testid="mobile-header-notifications"
            onClick={() => onOpenNotifications?.()}
            className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-zinc-600 transition-all active:scale-90 hover:bg-zinc-900/[0.06] hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-white/[0.07] dark:hover:text-white"
            aria-label="Notifications"
          >
            <Bell size={17} />
            {unreadCount > 0 ? (
              <span
                data-testid="mobile-header-notifications-badge"
                className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-[#c8ff00] px-1 text-[9px] font-bold leading-none text-black shadow-[0_0_0_2px_rgba(255,255,255,0.95)] dark:shadow-[0_0_0_2px_rgba(10,10,10,0.95)]"
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            ) : null}
          </button>

          {me ? (
            <div ref={profileRef} className="relative">
              <button
                data-testid="mobile-header-avatar-button"
                onClick={() => setProfileOpen((v) => !v)}
                className="relative flex items-center rounded-full transition-transform active:scale-95"
                aria-label="Profile"
              >
                <UserAvatar
                  testId="mobile-header-avatar"
                  user={me}
                  src={resolveAvatarUrl(me)}
                  size={36}
                />
                <span className="pointer-events-none absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500 dark:border-[#0a0a0a]" />
              </button>

              {profileOpen && (
                <div
                  data-testid="mobile-header-profile-popup"
                  className="absolute right-0 top-[calc(100%+10px)] z-50 w-[260px] origin-top-right overflow-hidden rounded-2xl border border-zinc-200/70 bg-white/95 p-2 shadow-[0_18px_50px_-12px_rgba(15,23,42,0.22)] backdrop-blur-xl dark:border-white/[0.07] dark:bg-[#0c0c0c]/95 dark:shadow-[0_20px_60px_rgba(0,0,0,0.65)]"
                >
                  <div className="flex items-center gap-3 rounded-xl px-2.5 py-2.5">
                    <UserAvatar user={me} src={resolveAvatarUrl(me)} size={42} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13.5px] font-semibold text-zinc-900 dark:text-white">
                        {me?.discord_username || me?.username || "—"}
                      </div>
                      <div className="truncate text-[11.5px] text-zinc-500 dark:text-zinc-500">
                        {me?.email || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="my-1 h-px bg-zinc-200/80 dark:bg-white/[0.06]" />

                  <button
                    data-testid="mobile-header-popup-theme"
                    onClick={(e) => { e.stopPropagation(); toggleTheme(); }}
                    className="flex w-full items-center justify-between rounded-lg px-2.5 py-2.5 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-900/[0.05] hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-white/[0.05] dark:hover:text-white"
                  >
                    <span className="flex items-center gap-2.5">
                      {theme === "dark" ? <Moon size={15} /> : <Sun size={15} />}
                      {theme === "dark" ? "Dark mode" : "Light mode"}
                    </span>
                    <span className="inline-flex h-5 w-9 items-center rounded-full bg-zinc-300 p-0.5 transition-colors dark:bg-zinc-700">
                      <span className={`h-4 w-4 rounded-full bg-white transition-transform ${theme === "dark" ? "translate-x-4" : "translate-x-0"}`} />
                    </span>
                  </button>
                  <button
                    data-testid="mobile-header-popup-profile"
                    onClick={() => { navigate("/profile"); setProfileOpen(false); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-900/[0.05] hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-white/[0.05] dark:hover:text-white"
                  >
                    <UserCircle2 size={15} /> Profile
                  </button>
                  <button
                    data-testid="mobile-header-popup-settings"
                    onClick={() => { navigate("/settings"); setProfileOpen(false); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-900/[0.05] hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-white/[0.05] dark:hover:text-white"
                  >
                    <Settings size={15} /> Settings
                  </button>
                  <button
                    data-testid="mobile-header-popup-help"
                    onClick={() => { navigate("/help"); setProfileOpen(false); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-900/[0.05] hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-white/[0.05] dark:hover:text-white"
                  >
                    <LifeBuoy size={15} /> Help
                  </button>

                  <div className="my-1 h-px bg-zinc-200/80 dark:bg-white/[0.06]" />

                  <button
                    data-testid="mobile-header-popup-logout"
                    onClick={() => logout()}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-[13px] font-medium text-rose-600 transition-colors hover:bg-rose-500/10 dark:text-rose-400 dark:hover:bg-rose-500/10"
                  >
                    <LogOut size={15} /> Log out
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
